"use client";

import { useEffect, useRef, useState, useCallback, memo } from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { Article } from "@/types";

type FeaturedSectionProps = {
  articles: Article[];
  autoScrollInterval?: number;
  enableAutoScroll?: boolean;
  className?: string;
};

// Featured article component
const FeaturedArticleCard = memo(({ article }: { article: Article }) => (
  <article className="relative mx-auto w-full overflow-hidden rounded-2xl bg-white shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-xl dark:bg-gray-800">
    <Link
      href={`/${article.slug}`}
      className="block rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      aria-label={`Lire l'article : ${article.title}`}
    >
      <div className="relative overflow-hidden">
        <Image
          src={article.cover_url || "/placeholder.jpg"}
          alt={`Image de l'article : ${article.title}`}
          width={800}
          height={500}
          className="h-[200px] w-full rounded-t-2xl object-cover transition-transform duration-500 hover:scale-105 sm:h-[350px] md:h-[450px]"
          priority
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = "/placeholder.jpg";
          }}
        />
        {/* Gradient overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
      </div>
      <div className="p-4 sm:p-6">
        <div className="mb-2 flex flex-wrap items-center gap-2 text-sm font-medium text-blue-600 dark:text-blue-400 sm:text-base">
          {article.authors && article.authors.length > 0 && (
            <>
              <span>{article.authors.map((a) => a.name).join(", ")}</span>
              <span>•</span>
            </>
          )}
          {article.categories && article.categories.length > 0 && (
            <>
              <span>{article.categories[0].name}</span>
              <span>•</span>
            </>
          )}
          <time dateTime={article.published_date}>
            {article.published_date}
          </time>
        </div>
        <h2
          className="mt-2 line-clamp-3 text-base font-semibold text-gray-900 dark:text-white sm:line-clamp-none sm:text-2xl"
          dangerouslySetInnerHTML={{ __html: article.title }}
        />
        {article.excerpt && (
          <p
            className="mt-3 line-clamp-2 text-sm text-gray-600 dark:text-gray-400 sm:text-base"
            dangerouslySetInnerHTML={{ __html: article.excerpt }}
          />
        )}
        <span className="mt-4 inline-flex items-center text-sm font-bold text-blue-600 transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 sm:text-base">
          Lire plus →
        </span>
      </div>
    </Link>
  </article>
));

FeaturedArticleCard.displayName = "FeaturedArticleCard";

// Article carousel item component
const CarouselArticleCard = memo(({ article }: { article: Article }) => (
  <article className="w-[180px] flex-shrink-0 sm:w-[220px] md:w-[250px] lg:w-[300px]">
    <Link
      href={`/${article.slug}`}
      className="block rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      aria-label={`Lire l'article : ${article.title}`}
    >
      <div className="relative flex h-full min-h-[280px] flex-col overflow-hidden rounded-2xl bg-white shadow-md transition-all duration-300 hover:scale-105 hover:shadow-lg dark:bg-gray-800 sm:min-h-[320px]">
        <div className="relative overflow-hidden">
          <Image
            src={article.cover_url || "/placeholder.jpg"}
            alt={`Image de l'article : ${article.title}`}
            width={300}
            height={200}
            className="h-[120px] w-full rounded-t-2xl object-cover transition-transform duration-300 hover:scale-105 sm:h-[150px] md:h-[180px]"
            loading="lazy"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = "/placeholder.jpg";
            }}
          />
        </div>
        <div className="flex flex-grow flex-col p-2 sm:p-4">
          <div className="mb-2 flex flex-wrap items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 sm:text-sm">
            {article.categories && article.categories.length > 0 && (
              <>
                <span>{article.categories[0].name}</span>
                <span>•</span>
              </>
            )}
            <time dateTime={article.published_date}>
              {article.published_date}
            </time>
          </div>
          <h3
            className="line-clamp-2 flex-grow text-sm font-semibold text-gray-900 transition-colors hover:text-blue-600 dark:text-white dark:hover:text-blue-400 sm:text-base"
            dangerouslySetInnerHTML={{ __html: article.title }}
          />
        </div>
      </div>
    </Link>
  </article>
));

CarouselArticleCard.displayName = "CarouselArticleCard";

const FeaturedSection: React.FC<FeaturedSectionProps> = ({
  articles,
  autoScrollInterval = 3000,
  enableAutoScroll = true,
  className = "",
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Check scroll position to enable/disable buttons
  const checkScrollPosition = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  // Automatic scrolling with better logic
  useEffect(() => {
    if (!scrollRef.current || !enableAutoScroll) return;
    if (window.innerWidth < 640 || isPaused) return;
    if (articles.length <= 1) return;

    const interval = setInterval(() => {
      if (!scrollRef.current) return;

      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      const maxScroll = scrollWidth - clientWidth;

      if (scrollLeft >= maxScroll) {
        // Reset to beginning smoothly
        scrollRef.current.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        // Scroll to next item
        scrollRef.current.scrollBy({ left: 300, behavior: "smooth" });
      }
    }, autoScrollInterval);

    return () => clearInterval(interval);
  }, [isPaused, enableAutoScroll, autoScrollInterval, articles.length]);

  // Manual navigation with better UX
  const scrollLeft = useCallback(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: -300, behavior: "smooth" });
    setTimeout(checkScrollPosition, 300);
  }, [checkScrollPosition]);

  const scrollRight = useCallback(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: 300, behavior: "smooth" });
    setTimeout(checkScrollPosition, 300);
  }, [checkScrollPosition]);

  // Enhanced touch navigation
  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      setTouchStartX(e.touches[0].clientX);
      setIsPaused(true);
    },
    [],
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (touchStartX === null) return;

      const touchDeltaX = e.changedTouches[0].clientX - touchStartX;
      const threshold = 50;

      if (Math.abs(touchDeltaX) > threshold) {
        if (touchDeltaX > 0 && canScrollLeft) {
          scrollLeft();
        } else if (touchDeltaX < 0 && canScrollRight) {
          scrollRight();
        }
      }

      setTouchStartX(null);
      setTimeout(() => setIsPaused(false), 1000);
    },
    [touchStartX, canScrollLeft, canScrollRight, scrollLeft, scrollRight],
  );

  // Handle scroll events
  const handleScroll = useCallback(() => {
    checkScrollPosition();
  }, [checkScrollPosition]);

  // Initialize scroll position check
  useEffect(() => {
    checkScrollPosition();
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleScroll);
      return () => scrollElement.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll, checkScrollPosition]);

  // Early return if no articles
  if (!articles || articles.length === 0) {
    return null;
  }

  // Separate featured article from the rest
  const [featuredArticle, ...otherArticles] = articles;

  return (
    <div className={`relative w-full px-4 ${className}`}>
      {/* Featured Article */}
      <FeaturedArticleCard article={featuredArticle} />

      {/* Carousel of Latest Articles */}
      {otherArticles.length > 0 && (
        <section
          className="relative mt-6"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          aria-labelledby="latest-articles-heading"
        >
          <header className="mb-4 flex items-center justify-between">
            <h3
              id="latest-articles-heading"
              className="text-lg font-semibold text-gray-900 dark:text-white"
            >
              Dernières Publications
            </h3>
            <div className="flex gap-2 sm:hidden">
              <button
                onClick={scrollLeft}
                disabled={!canScrollLeft}
                className="rounded-full border bg-white p-2 shadow-md transition-all hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-800"
                aria-label="Article précédent"
              >
                <ChevronLeftIcon className="h-4 w-4 text-gray-600 dark:text-white" />
              </button>
              <button
                onClick={scrollRight}
                disabled={!canScrollRight}
                className="rounded-full border bg-white p-2 shadow-md transition-all hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-800"
                aria-label="Article suivant"
              >
                <ChevronRightIcon className="h-4 w-4 text-gray-600 dark:text-white" />
              </button>
            </div>
          </header>

          <div
            ref={scrollRef}
            className="scrollbar-hide flex gap-4 overflow-x-auto scroll-smooth pb-2"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            role="region"
            aria-label="Articles en carrousel"
          >
            {otherArticles.map((article, index) => (
              <CarouselArticleCard
                key={`${article.id}-${index}`}
                article={article}
              />
            ))}
          </div>

          {/* Desktop Navigation Buttons */}
          <button
            onClick={scrollLeft}
            disabled={!canScrollLeft}
            className="absolute -left-8 top-1/2 hidden -translate-y-1/2 transform rounded-full border bg-white p-2 shadow-lg transition-all hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-800 lg:block"
            aria-label="Défiler vers la gauche"
          >
            <ChevronLeftIcon className="h-6 w-6 text-gray-600 dark:text-white" />
          </button>
          <button
            onClick={scrollRight}
            disabled={!canScrollRight}
            className="absolute -right-8 top-1/2 hidden -translate-y-1/2 transform rounded-full border bg-white p-2 shadow-lg transition-all hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-800 lg:block"
            aria-label="Défiler vers la droite"
          >
            <ChevronRightIcon className="h-6 w-6 text-gray-600 dark:text-white" />
          </button>
        </section>
      )}
    </div>
  );
};

export default memo(FeaturedSection);
